/**
 * Review tab - pending draft review queue.
 *
 * Left:  list of drafts filtered by status (default: pending_review)
 * Right: selected draft with full editable subject + body, action buttons.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, RefreshCw, Search, CheckCircle2, XCircle, Wand2, Save,
} from 'lucide-react';

import type { ApiCall } from '../../api/interview';
import {
  type Draft, type Campaign,
  listDrafts, listCampaigns, getDraft,
  updateDraft, approveDraft, rejectDraft, regenerateDraft,
} from '../../api/b2b';
import { Centered, ErrorBox, HeadingWithInfo, ActionWithInfo } from './_ui';


interface Props { apiCall: ApiCall; }


const STATUS_OPTIONS = [
  { id: 'pending_review',  label: 'Pending review' },
  { id: 'auto_approved',   label: 'Auto-approved' },
  { id: 'human_approved',  label: 'Human-approved' },
  { id: 'rejected',        label: 'Rejected' },
  { id: 'sent',            label: 'Sent' },
  { id: '',                label: 'All' },
];


const B2BTabReview: React.FC<Props> = ({ apiCall }) => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [campaignFilter, setCampaignFilter] = useState<number | ''>('');
  const [minConfidence, setMinConfidence] = useState<number | ''>('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, c] = await Promise.all([
        listDrafts(apiCall, {
          status: statusFilter || undefined,
          campaign_id: campaignFilter === '' ? undefined : Number(campaignFilter),
          min_confidence: minConfidence === '' ? undefined : Number(minConfidence),
          search: search || undefined,
          limit: 100,
        }),
        listCampaigns(apiCall, { include_stats: false }),
      ]);
      setDrafts(d);
      setCampaigns(c);
    } catch (e: any) {
      setError(e?.message || 'Failed to load drafts');
    } finally {
      setLoading(false);
    }
  }, [apiCall, statusFilter, campaignFilter, minConfidence, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      {/* LEFT - list */}
      <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-3 border-b border-slate-100 space-y-2">
          <div className="flex gap-2 items-center">
            <HeadingWithInfo
              className="text-xs font-medium text-slate-600 shrink-0"
              info="Email-Entwürfe prüfen bevor Send batch. Approve → erscheint in Send-Queue der Kampagne."
            >
              Draft queue
            </HeadingWithInfo>
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search subject / email"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded-md"
              />
            </div>
            <button onClick={load} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50">
              <RefreshCw className="w-4 h-4 text-slate-600" />
            </button>
          </div>
          <div className="flex gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="text-xs border border-slate-200 rounded-md px-2 py-1.5 flex-1">
              {STATUS_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value === '' ? '' : Number(e.target.value))}
                    className="text-xs border border-slate-200 rounded-md px-2 py-1.5 flex-1">
              <option value="">All campaigns</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <input type="number" min={0} max={1} step={0.05} placeholder="min conf"
                   value={minConfidence}
                   onChange={(e) => setMinConfidence(e.target.value === '' ? '' : Number(e.target.value))}
                   className="w-20 text-xs border border-slate-200 rounded-md px-2 py-1.5" />
          </div>
        </div>

        {loading ? (
          <Centered><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></Centered>
        ) : error ? (
          <ErrorBox message={error} onRetry={load} />
        ) : drafts.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            No drafts match. Run <code className="font-mono text-[11px]">generate_drafts</code> to populate.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-[75vh] overflow-y-auto">
            {drafts.map((d) => (
              <li
                key={d.id}
                onClick={() => setSelectedId(d.id)}
                className={`px-3 py-2 cursor-pointer hover:bg-slate-50 ${
                  selectedId === d.id ? 'bg-pink-50' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {d.subject || '(no subject)'}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {d.contact_email}
                      {d.company_domain && ` · ${d.company_domain}`}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 text-[10px]">
                    <StatusBadge status={d.status} />
                    {d.confidence_score != null && (
                      <span className="text-slate-500">
                        conf {(d.confidence_score * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* RIGHT - detail */}
      <div className="lg:col-span-3">
        {selectedId == null ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-400 text-sm">
            Select a draft to review
          </div>
        ) : (
          <DraftDetailPanel
            apiCall={apiCall}
            draftId={selectedId}
            onChanged={load}
          />
        )}
      </div>
    </div>
  );
};

export default B2BTabReview;


// ============================================================
// Detail panel
// ============================================================

const DraftDetailPanel: React.FC<{
  apiCall: ApiCall;
  draftId: number;
  onChanged: () => void;
}> = ({ apiCall, draftId, onChanged }) => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getDraft(apiCall, draftId);
      setDraft(d);
      setEditedSubject(d.edited_subject || d.subject || '');
      setEditedBody(d.edited_body || d.body_text || '');
    } catch (e: any) {
      setError(e?.message || 'Failed to load draft');
    } finally {
      setLoading(false);
    }
  }, [apiCall, draftId]);

  useEffect(() => { load(); }, [load]);

  const subjectDirty = useMemo(
    () => draft != null && editedSubject !== (draft.edited_subject ?? draft.subject ?? ''),
    [draft, editedSubject],
  );
  const bodyDirty = useMemo(
    () => draft != null && editedBody !== (draft.edited_body ?? draft.body_text ?? ''),
    [draft, editedBody],
  );

  const saveEdits = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const payload: { edited_subject?: string; edited_body?: string } = {};
      if (subjectDirty) payload.edited_subject = editedSubject;
      if (bodyDirty)    payload.edited_body    = editedBody;
      await updateDraft(apiCall, draft.id, payload);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const doApprove = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      if (subjectDirty || bodyDirty) await saveEdits();
      await approveDraft(apiCall, draft.id);
      await load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!draft) return;
    const reason = window.prompt('Reason (optional):') || undefined;
    setBusy(true);
    try {
      await rejectDraft(apiCall, draft.id, reason);
      await load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const doRegenerate = async () => {
    if (!draft) return;
    if (!window.confirm('Regenerate this draft via LLM? Current edits will be lost.')) return;
    setBusy(true);
    try {
      await regenerateDraft(apiCall, draft.id);
      await load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="bg-white rounded-lg border border-slate-200 p-6"><Centered><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></Centered></div>;
  if (error)   return <ErrorBox message={error} onRetry={load} />;
  if (!draft)  return <div className="bg-white rounded-lg border border-slate-200 p-6 text-sm text-slate-400">Draft not found</div>;

  const isLocked = draft.status === 'sent' || draft.status === 'failed';

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 max-h-[80vh] overflow-y-auto space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-slate-500">
            {draft.campaign_name && <span className="font-medium text-slate-700">{draft.campaign_name}</span>}
            {draft.confidence_score != null && (
              <span className="ml-2">conf {(draft.confidence_score * 100).toFixed(0)}%</span>
            )}
          </div>
          <div className="text-sm text-slate-700 mt-1">
            <span className="text-slate-500">To: </span>
            <span className="font-medium">{draft.contact_email}</span>
            {draft.contact_first_name && <span> · {draft.contact_first_name} {draft.contact_last_name}</span>}
            {draft.contact_title && <span className="text-slate-500"> · {draft.contact_title}</span>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{draft.company_name || draft.company_domain}</div>
        </div>
        <StatusBadge status={draft.status} />
      </div>

      <div>
        <div className="text-xs text-slate-500 mb-1">Subject</div>
        <input
          type="text"
          value={editedSubject}
          onChange={(e) => setEditedSubject(e.target.value)}
          disabled={isLocked}
          className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 font-medium disabled:bg-slate-50"
        />
        {subjectDirty && <div className="text-[10px] text-amber-600 mt-0.5">unsaved edits</div>}
      </div>

      <div>
        <div className="text-xs text-slate-500 mb-1">Body</div>
        <textarea
          value={editedBody}
          onChange={(e) => setEditedBody(e.target.value)}
          disabled={isLocked}
          rows={18}
          className="w-full text-sm border border-slate-200 rounded px-2 py-2 font-mono disabled:bg-slate-50"
        />
        {bodyDirty && <div className="text-[10px] text-amber-600 mt-0.5">unsaved edits</div>}
      </div>

      {!isLocked && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {(subjectDirty || bodyDirty) && (
            <ActionWithInfo info="Subject/Body speichern ohne freizugeben.">
              <button
                onClick={saveEdits}
                disabled={busy}
                className="px-3 py-1.5 text-sm bg-slate-800 text-white rounded hover:bg-slate-700 disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Save className="w-3.5 h-3.5" /> Save edits
              </button>
            </ActionWithInfo>
          )}
          <ActionWithInfo info="Freigeben - Draft kann per Send batch versendet werden.">
            <button
              onClick={doApprove}
              disabled={busy}
              className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve
            </button>
          </ActionWithInfo>
          <ActionWithInfo info="Draft verwerfen - wird nicht versendet.">
            <button
              onClick={doReject}
              disabled={busy}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <XCircle className="w-3.5 h-3.5" /> Reject
            </button>
          </ActionWithInfo>
          <ActionWithInfo info="LLM schreibt den Draft neu - aktuelle Edits gehen verloren.">
            <button
              onClick={doRegenerate}
              disabled={busy}
              className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Wand2 className="w-3.5 h-3.5" /> Regenerate
            </button>
          </ActionWithInfo>
          {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>
      )}

      {draft.reviewer_notes && (
        <div className="text-xs text-slate-500 italic pt-2 border-t border-slate-100">
          Note: {draft.reviewer_notes}
        </div>
      )}
    </div>
  );
};


const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { color: string; label: string }> = {
    pending_review:  { color: 'bg-amber-100 text-amber-700',     label: 'Pending' },
    auto_approved:   { color: 'bg-blue-100 text-blue-700',       label: 'Auto-approved' },
    human_approved:  { color: 'bg-emerald-100 text-emerald-700', label: 'Approved' },
    rejected:        { color: 'bg-red-100 text-red-700',         label: 'Rejected' },
    sent:            { color: 'bg-slate-200 text-slate-700',     label: 'Sent' },
    failed:          { color: 'bg-red-200 text-red-800',         label: 'Failed' },
  };
  const v = map[status] || { color: 'bg-slate-100 text-slate-500', label: status };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${v.color}`}>
      {v.label}
    </span>
  );
};
